<script setup>
/*
    Audio widget - play sound

    Components:
        primary: <v-btn>

    Widget Properties:
        url: string | map | list
            Maps: { value: url }
            List: { expression, url}
            # https://www.embedthis.com/images/sounds/klaxon.mp3
        prefix: icon (defaults to $play/$pause)

    CSS Properties:
        color
        background
        font-size
        font-weight

    Custom CSS Properties
        --w-autoplay
        --w-duration
        --w-loop
 */

import {reactive, ref} from 'vue'
import {getWidgetProps, pickItem} from '@/paks/vu-dash/BoardUtils'
import {State} from '@/paks/vu-app'

defineExpose({update})

const props = defineProps({widget: Object})

const page = reactive({
    autoplay: false,
    args: {},
    audio: null,
    data: {},
    duration: null,
    icon: null,
    loop: false,
    playing: false,
})

const primary = ref(null)

const DefaultStyles = {
    primary: {
        background: 'rgb(var(--v-theme-primary))',
        color: 'rgb(var(--v-theme-on-primary))',
        'font-size': 'max(10px, min(min(9cqw, 20cqh), 144px))',
        'font-weight': 'bold',
    },
}

function update() {
    let widget = props.widget
    Object.assign(page, getWidgetProps(
        widget,
        {primary},
        {args: {variant: 'flat'}, styles: DefaultStyles}
    ))
    page.icon = widget.prefix

    let {args, styles} = page
    if (styles.color && !args.color) {
        // Need to map color to the vuetify v-btn color arg
        args.color = styles.background
    }
    page.duration = args.duration || Number.MAX_SAFE_INTEGER / 1000
    page.autoplay = args.autoplay || false
    page.loop = args.loop || false

    if (page.audio && page.playing) {
        page.audio.pause()
    }
    let item = pickItem(widget.url, {
        context: {inputs: State.context.inputs, value: widget.value || widget.defaultValue},
    })
    page.audio = new Audio(item.value)
    if (page.autoplay) {
        play()
    }
}

function play() {
    let audio = page.audio
    if (page.playing) {
        page.audio.pause()
        page.playing = false
    } else {
        audio.play()
        page.playing = true
        if (page.loop) {
            audio.addEventListener('ended', () => {
                audio.currentTime = 0
                audio.play()
                setDuration()
            })
        }
        setDuration()
    }
}

function setDuration() {
    setTimeout(() => {
        if (page.playing) {
            page.audio.pause()
            page.playing = false
        }
    }, page.duration * 1000)
}
</script>

<template>
    <v-btn
        v-if="!page.autoplay"
        ref="primary"
        class="audio-widget"
        :prepend-icon="page.icon || (page.playing ? '$pause' : '$play')"
        @click="play">
        {{ page.data.primary || 'Play' }}
    </v-btn>
    <div v-else />
</template>

<style lang="scss" scoped>
.audio-widget {
    container-type: size;
    position: relative;
    height: 100cqh !important;
    width: 100cqw !important;
    text-align: center;
    top: 50cqh;
    transform: translateY(-50%);
}
</style>
